import React from "react";
import { Card, Col, Row } from "antd";
import {
  ClockCircleOutlined,
  WalletOutlined,
  RiseOutlined,
  MinusCircleOutlined,
  FileTextOutlined,
  FundOutlined,
} from "@ant-design/icons";
import { formatPayoutAmount, PayoutCurrencyTotals } from "../../utils/payoutDisplay";

type PayoutCurrencySummaryCardProps = {
  totals: PayoutCurrencyTotals;
  summaryLabel?: string;
  /** Organizer's commission rate for this event, e.g. from event.commission / organization.commission. Falls back to a value derived from totals when omitted (e.g. on the aggregated workspace view). */
  organizerChargePercentage?: number;
  /** true when the event is configured so the participant (ticket buyer) bears the organizer charge, i.e. event.add_convenience_fees. Falls back to totals.organizer_charge === 0 when omitted. */
  payByParticipant?: boolean;
  /** Set false on aggregated/multi-event views where a single percentage and pay-by setting don't apply. Defaults to true. */
  showChargeDetails?: boolean;
};

type HeroStatCardProps = {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  highlight?: boolean;
};

const HeroStatCard = ({
  label,
  value,
  icon,
  iconColor,
  iconBg,
  highlight = false,
}: HeroStatCardProps) => (
  <div
    style={{
      border: highlight ? "1.5px solid #7cb342" : "1.5px solid #b5c99a",
      borderRadius: 10,
      background: highlight
        ? "linear-gradient(135deg, #f6ffed 0%, #f0fce8 100%)"
        : "#ffffff",
      padding: "10px 10px",
      textAlign: "center",
      height: "100%",
      minHeight: 76,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
    }}
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        height: 24,
        borderRadius: "50%",
        background: iconBg,
        color: iconColor,
        fontSize: 12,
      }}
    >
      {icon}
    </span>
    <div style={{ fontSize: 12, fontWeight: 600, color: "#262626", lineHeight: 1.25 }}>
      {label}
    </div>
    <div
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: highlight ? "#135200" : "#1a1a2e",
      }}
    >
      {value}
    </div>
  </div>
);

const PayByBadge = ({ label }: { label: string }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: 15,
      fontWeight: 700,
      color: "#531dab",
      background: "#f9f0ff",
      border: "1px solid #d3adf7",
      borderRadius: 999,
      padding: "5px 16px",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
);

const formatPercent = (value?: number | null) => {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? `${n}` : `${parseFloat(n.toFixed(2))}`;
};

const PayoutCurrencySummaryCard = ({
  totals,
  summaryLabel = "Payout Summary",
  organizerChargePercentage,
  payByParticipant,
  showChargeDetails = true,
}: PayoutCurrencySummaryCardProps) => {
  const currency = "INR";

  // Use backend-provided `display_total_revenue` when present. This ensures that
  // when convenience fees were paid by the customer we show the organizer's
  // actual commission (excluding customer-paid fees) in the UI.
  const totalRevenue =
    totals.display_total_revenue ?? totals.total_customer_revenue ?? totals.total_commission;
  const netPayout = totals.organizer_earnings || totals.total_payout_amount;

  const isChargeZero =
    showChargeDetails &&
    (payByParticipant === true ||
      (payByParticipant === undefined && totals.organizer_charge === 0));

  const derivedChargePercentage =
    totalRevenue > 0 ? (totals.organizer_charge / totalRevenue) * 100 : 0;
  const chargePercentage = organizerChargePercentage ?? derivedChargePercentage;

  const chargeLabel = !showChargeDetails
    ? "Organizer Charges"
    : isChargeZero
      ? "Organizer Charges"
      : `Organizer Charges ${formatPercent(chargePercentage)}%`;

  const gstLabel = !showChargeDetails
    ? "Organizer Charges GST"
    : isChargeZero || totals.tax_on_organizer_charge === 0
      ? "Organizer Charges GST"
      : `Organizer Charges GST ${formatPercent(totals.tax_percentage || 0)}%`;

  const payByOrganizer =
    payByParticipant !== undefined ? !payByParticipant : totals.organizer_charge > 0;
  const payByLabel = payByOrganizer
    ? "Pay By You"
    : "Organizer Charges Pay By Customer";

  const secondaryStats = [
    {
      label: "Requested",
      value: formatPayoutAmount(currency, totals.already_requested),
      icon: <ClockCircleOutlined size={20} />,
      iconColor: "#08979c",
      iconBg: "#e6fffb",
    },
    {
      label: "Available Payout",
      value: formatPayoutAmount(currency, totals.available_payout),
      icon: <WalletOutlined size={20} />,
      iconColor: "#389e0d",
      iconBg: "#d9f7be",
      highlight: true,
    },
    ...(totals?.total_refund && totals.total_refund !== 0
      ? [
        {
          label: "Total Refunds",
          value: formatPayoutAmount(currency, totals.total_refund),
          icon: <WalletOutlined size={20} />,
          iconColor: "#1677ff",
          iconBg: "#e6f4ff",
        },
      ]
      : []),
  ];

  return (
    <Card
      bordered={false}
      className="w-full"
      bodyStyle={{ padding: 18 }}
      style={{
        borderRadius: 14,
        border: "1px solid #f0f0f0",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 14,
        }}
      >
        {showChargeDetails && <PayByBadge label={payByLabel} />}
        <div
          style={{
            fontSize: 12,
            color: "#9ca3af",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {summaryLabel}
        </div>
      </div>

      <Row gutter={[10, 10]} align="stretch">
        <Col xs={12} md={6}>
          <HeroStatCard
            label="Total Revenue"
            value={formatPayoutAmount(currency, totalRevenue)}
            icon={<RiseOutlined />}
            iconColor="#1677ff"
            iconBg="#e6f4ff"
          />
        </Col>
        <Col xs={12} md={6}>
          <HeroStatCard
            label={chargeLabel}
            value={formatPayoutAmount(currency, totals.organizer_charge)}
            icon={<MinusCircleOutlined />}
            iconColor="#cf1322"
            iconBg="#fff1f0"
          />
        </Col>
        <Col xs={12} md={6}>
          <HeroStatCard
            label={gstLabel}
            value={formatPayoutAmount(currency, totals.tax_on_organizer_charge)}
            icon={<FileTextOutlined />}
            iconColor="#d48806"
            iconBg="#fffbe6"
          />
        </Col>
        <Col xs={12} md={6}>
          <HeroStatCard
            label="Net Payout"
            value={formatPayoutAmount(currency, netPayout)}
            icon={<FundOutlined />}
            iconColor="#389e0d"
            iconBg="#d9f7be"
            highlight
          />
        </Col>
      </Row>

      <Row gutter={[10, 10]} align="stretch" style={{ marginTop: 12 }}>
        {secondaryStats.map((item, index) => (
          <Col xs={12} md={6} key={index}>
            <HeroStatCard
              label={item.label}
              value={item.value}
              icon={item.icon}
              iconColor={item.iconColor}
              iconBg={item.iconBg}
              highlight={item.highlight}
            />
          </Col>
        ))}
      </Row>
    </Card>
  );
};

export default PayoutCurrencySummaryCard;
